import { useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { Container } from "@egalregal/shared";
import { PrintSheet } from "./PrintSheet";
import { LabelCell, qrEdgeMm } from "../../shared/components/LabelCell";
import type { LabelConfig, MarginMode } from "../../shared/components/LabelCell";
import { ChevronDownIcon, ChevronUpIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";

export const GRID_MIN = 1;
export const GRID_COLUMNS_MAX = 32;
export const GRID_ROWS_MAX = 64;
export const GRID_DEFAULT = 3;

/**
 * Paper output palette (ticket 13). The print portal is portalled outside the
 * app root, so it inherits the body colour from CssBaseline: under a dark
 * theme that is near-white ink, invisible on white paper. Scoping the light
 * theme here (plus an explicit text colour on the portal) keeps the printed
 * sheet ink-on-paper whatever the app theme is, while the on-screen preview
 * keeps following the active theme.
 */
const PRINT_PAPER_THEME = createTheme({ palette: { mode: "light" } });

/**
 * Clamp the columns input to the 1–32 range.
 *
 * @param n the raw numeric input (may be out of range or non-finite).
 * @returns the input floored and clamped to [GRID_MIN, GRID_COLUMNS_MAX].
 */
export function clampColumns(n: number): number {
  return clampGridValue(n, GRID_COLUMNS_MAX);
}

/**
 * Clamp the rows input to the 1–64 range.
 *
 * @param n the raw numeric input (may be out of range or non-finite).
 * @returns the input floored and clamped to [GRID_MIN, GRID_ROWS_MAX].
 */
export function clampRows(n: number): number {
  return clampGridValue(n, GRID_ROWS_MAX);
}

/**
 * Clamp a grid dimension to its range, flooring fractional input.
 *
 * @param n the raw numeric input (may be out of range or non-finite).
 * @param max the upper bound for this dimension.
 * @returns the input floored and clamped to [GRID_MIN, max].
 */
function clampGridValue(n: number, max: number): number {
  if (!Number.isFinite(n)) return GRID_MIN;
  return Math.min(max, Math.max(GRID_MIN, Math.floor(n)));
}

/**
 * One numeric mm input for a margin mode or the gap — same conventions as the
 * original spacing steppers (min 0, step 0.5).
 */
function mmField(label: string, value: number, onChange: (n: number) => void) {
  return (
    <TextField
      type="number"
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
      sx={{ minWidth: 120 }}
    />
  );
}

interface PrintSheetDialogProps {
  open: boolean;
  containers: Container[];
  onClose: () => void;
  /** Blank-labels mode (ticket 30): print QR-only sheets of fresh UUIDs. */
  blank?: boolean;
}

/**
 * Live single-label preview + columns × rows pickers (default 3×3, columns
 * 1–32, rows 1–64) and the browser print dialog (ADR-0001, ticket 09, ticket
 * 06, ticket 05, ticket 07). The on-screen preview renders exactly one label — the first
 * picked container's, or a freshly minted UUID in blank mode — with the live
 * LabelConfig, so label content and spacing can be judged at a fixed size
 * independent of the grid. While open, a print-only portal carries the full
 * `columns × rows` sheet to the printer; on screen it stays hidden.
 *
 * In blank mode (ticket 30) the sheet renders QR-only cells with freshly
 * minted UUIDs — the user prints them on sticky paper and registers each
 * container later by scanning its label (the create form is prefilled with
 * the scanned UUID). Blank mode prints exactly one full sheet, i.e.
 * `columns × rows` labels; there is no separate count picker.
 */
export function PrintSheetDialog({ open, containers, onClose, blank = false }: PrintSheetDialogProps) {
  const t = useT();
  // Raw input strings so the fields stay editable (clearable, intermediate
  // states); the sheet always uses the clamped values, and blur normalizes.
  const [columnsRaw, setColumnsRaw] = useState(String(GRID_DEFAULT));
  const [rowsRaw, setRowsRaw] = useState(String(GRID_DEFAULT));
  const [layout, setLayout] = useState<LabelConfig["layout"]>("stack");
  const [showIcons, setShowIcons] = useState(true);
  const [showQr, setShowQr] = useState(true);
  const [showShortId, setShowShortId] = useState(true);
  const [marginMm, setMarginMm] = useState(1);
  // One value per margin mode (ticket 07): distinct state vars make switching
  // modes restore each mode's last entered values without any bookkeeping.
  const [marginMode, setMarginMode] = useState<MarginMode>("uniform");
  const [marginXMm, setMarginXMm] = useState(1);
  const [marginYMm, setMarginYMm] = useState(1);
  const [marginTopMm, setMarginTopMm] = useState(1);
  const [marginRightMm, setMarginRightMm] = useState(1);
  const [marginBottomMm, setMarginBottomMm] = useState(1);
  const [marginLeftMm, setMarginLeftMm] = useState(1);
  const [gapMm, setGapMm] = useState(1);
  // Margin/gap are advanced controls most users never touch: they sit behind
  // one shared text toggle, collapsed on every dialog open (ticket 06). The
  // state itself never resets the values — collapsing only unmounts the inputs.
  const [spacingExpanded, setSpacingExpanded] = useState(false);
  // One preview UUID per dialog session (ticket 05): minted when the dialog
  // opens, stable across every control change while it stays open, reminted
  // on the next open. Adjusted during render (React's documented pattern) so
  // a reopened dialog never shows the previous session's label first.
  const [previewUuid, setPreviewUuid] = useState(() => crypto.randomUUID());
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPreviewUuid(crypto.randomUUID());
  }
  const columns = clampColumns(Number(columnsRaw));
  const rows = clampRows(Number(rowsRaw));

  const config: LabelConfig = {
    layout,
    showIcons,
    showQr,
    showShortId,
    marginMm,
    gapMm,
    marginMode,
    // Only the active mode's fields travel with the config; resolveMargins
    // falls back to marginMm for anything absent.
    ...(marginMode === "xy" ? { marginXMm, marginYMm } : {}),
    ...(marginMode === "edges"
      ? { marginTopMm, marginRightMm, marginBottomMm, marginLeftMm }
      : {}),
  };

  const hasBreach = config.showQr && qrEdgeMm(config, columns) < 6;

  const sheet = blank ? (
    <PrintSheet containers={[]} columns={columns} rows={rows} blankCount={columns * rows} config={config} />
  ) : (
    <PrintSheet containers={containers} columns={columns} rows={rows} config={config} />
  );

  const first = !blank && containers.length > 0 ? containers[0]! : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("printTitle")}</DialogTitle>
        {/* MUI removes DialogContent's top padding when a DialogTitle sits above
            it; the first fields' floating labels then straddle the scroll edge
            and clip. Restore the normal top gap. */}
        <DialogContent sx={{ "&.MuiDialogContent-root": { pt: 2.5 } }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2, flexWrap: "wrap" }}>
            <TextField
              type="number"
              size="small"
              label={t("printColumns")}
              value={columnsRaw}
              onChange={(e) => setColumnsRaw(e.target.value)}
              onBlur={(e) => setColumnsRaw(String(clampColumns(Number(e.target.value))))}
              slotProps={{ htmlInput: { min: GRID_MIN, max: GRID_COLUMNS_MAX, step: 1 } }}
              sx={{ minWidth: 120 }}
            />
            <TextField
              type="number"
              size="small"
              label={t("printRows")}
              value={rowsRaw}
              onChange={(e) => setRowsRaw(e.target.value)}
              onBlur={(e) => setRowsRaw(String(clampRows(Number(e.target.value))))}
              slotProps={{ htmlInput: { min: GRID_MIN, max: GRID_ROWS_MAX, step: 1 } }}
              sx={{ minWidth: 120 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset" size="small">
              <FormLabel>{t("printLayout")}</FormLabel>
              <RadioGroup
                row
                value={layout}
                onChange={(e) => setLayout(e.target.value as LabelConfig["layout"])}
              >
                <FormControlLabel value="stack" control={<Radio />} label={t("printLayoutStack")} />
                <FormControlLabel value="row" control={<Radio />} label={t("printLayoutRow")} />
                <FormControlLabel value="split" control={<Radio />} label={t("printLayoutSplit")} />
              </RadioGroup>
            </FormControl>
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Checkbox checked={showIcons} onChange={(e) => setShowIcons(e.target.checked)} />}
                label={t("printShowIcons")}
              />
              <FormControlLabel
                control={<Checkbox checked={showQr} onChange={(e) => setShowQr(e.target.checked)} />}
                label={t("printShowQr")}
              />
              <FormControlLabel
                control={<Checkbox checked={showShortId} onChange={(e) => setShowShortId(e.target.checked)} />}
                label={t("printShowShortId")}
              />
            </Box>
            <Button
              size="small"
              sx={{ mt: 1, px: 0 }}
              data-testid="spacing-toggle"
              aria-expanded={spacingExpanded}
              onClick={() => setSpacingExpanded((expanded) => !expanded)}
              endIcon={spacingExpanded ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
            >
              {t("printCustomizeSpacing")}
            </Button>
            {spacingExpanded && (
              <Box data-testid="spacing-section" sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                <FormControl component="fieldset" size="small">
                  <FormLabel>{t("printMarginMode")}</FormLabel>
                  <RadioGroup
                    row
                    value={marginMode}
                    onChange={(e) => setMarginMode(e.target.value as MarginMode)}
                  >
                    <FormControlLabel value="uniform" control={<Radio />} label={t("printMarginModeUniform")} />
                    <FormControlLabel value="xy" control={<Radio />} label={t("printMarginModeXy")} />
                    <FormControlLabel value="edges" control={<Radio />} label={t("printMarginModeEdges")} />
                  </RadioGroup>
                </FormControl>
                {marginMode === "uniform" && mmField(t("printMarginMm"), marginMm, setMarginMm)}
                {marginMode === "xy" && (
                  <>
                    {mmField(t("printMarginX"), marginXMm, setMarginXMm)}
                    {mmField(t("printMarginY"), marginYMm, setMarginYMm)}
                  </>
                )}
                {marginMode === "edges" && (
                  <>
                    {mmField(t("printMarginTop"), marginTopMm, setMarginTopMm)}
                    {mmField(t("printMarginRight"), marginRightMm, setMarginRightMm)}
                    {mmField(t("printMarginBottom"), marginBottomMm, setMarginBottomMm)}
                    {mmField(t("printMarginLeft"), marginLeftMm, setMarginLeftMm)}
                  </>
                )}
                {mmField(t("printGapMm"), gapMm, setGapMm)}
              </Box>
            )}
            {hasBreach && (
              <Typography color="warning.main" variant="caption" sx={{ mt: 1, display: "block" }}>
                {t("printLegibilityBreach")}
              </Typography>
            )}
          </Box>
          {/* Fixed phone-card scale (ticket 05): the preview must not follow
              the columns/rows pickers, and the portal must not pick up this
              width — it renders its own PrintSheet below. */}
          <Box sx={{ maxWidth: 320, mx: "auto", my: 2 }}>
            <LabelCell
              uuid={first ? first.id : previewUuid}
              name={first ? first.name : ""}
              className="print-cell"
              config={config}
              columns={columns}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button variant="contained" onClick={() => window.print()}>
            {t("print")}
          </Button>
        </DialogActions>
      </Dialog>

      {open && createPortal(
        <ThemeProvider theme={PRINT_PAPER_THEME}>
          <Box component="span">
            <style>{`
              .print-sheet-portal { display: none; }
              @media print {
                #root, .MuiDialog-root { display: none !important; }
                .print-sheet-portal { display: block !important; }
              }
            `}</style>
            <Box className="print-sheet-portal" sx={{ color: "text.primary" }}>
              {sheet}
            </Box>
          </Box>
        </ThemeProvider>,
        document.body,
      )}
    </>
  );
}